<template>
  <div class="whisper-test" @keydown="handleKeyDown" @keyup="handleKeyUp" tabindex="0">
    <div class="container">
      <h1>Whisper 음성 인식 테스트</h1>
      
      <!-- 키보드 안내 -->
      <div class="keyboard-guide">
        <p><strong>🎯 빠른 조작:</strong> 스페이스바를 누르고 있는 동안 녹음됩니다</p>
        <p class="space-status" :class="{ active: isSpacePressed }">
          {{ isSpacePressed ? '🔴 스페이스바 눌림 - 녹음 중' : '⚪ 스페이스바를 눌러 녹음 시작' }}
        </p>
      </div>
      
      <!-- 파일 업로드 섹션 -->
      <div class="upload-section">
        <h2>파일 업로드</h2>
        <div class="upload-area" @drop="handleFileDrop" @dragover.prevent @dragenter.prevent>
          <input 
            ref="fileInput" 
            type="file" 
            accept="audio/*" 
            @change="handleFileSelect" 
            style="display: none"
          />
          <button @click="$refs.fileInput.click()" class="upload-btn">
            오디오 파일 선택
          </button>
          <p>또는 파일을 여기로 드래그하세요</p>
        </div>
        
        <div v-if="selectedFile" class="file-info">
          <p>선택된 파일: {{ selectedFile.name }}</p>
          <button @click="transcribeFile" :disabled="isTranscribing" class="transcribe-btn">
            {{ isTranscribing ? '변환 중...' : '텍스트 변환' }}
          </button>
        </div>
      </div>

      <!-- 실시간 녹음 섹션 -->
      <div class="recording-section">
        <h2>실시간 음성 인식</h2>
        <div class="recording-controls">
          <button 
            @click="toggleRecording" 
            :class="['record-btn', { recording: isRecording }]"
            :disabled="isInitializing"
          >
            {{ isRecording ? '녹음 중지' : '녹음 시작' }}
          </button>
          <span v-if="isRecording" class="recording-indicator">🔴 녹음 중...</span>
        </div>
      </div>

      <!-- 결과 표시 섹션 -->
      <div class="results-section" v-if="transcription">
        <div class="results-header">
          <h2>변환 결과</h2>
          <button @click="clearResults" class="clear-results-btn">결과 지우기</button>
        </div>
        <div class="transcription-result">
          <p>{{ transcription }}</p>
        </div>
        <div v-if="realTimeResults.length > 1" class="real-time-chunks">
          <h3>실시간 변환 구간별 결과:</h3>
          <div v-for="(result, index) in realTimeResults" :key="index" class="chunk-result">
            <span class="chunk-number">{{ index + 1 }}.</span> {{ result }}
          </div>
        </div>
      </div>

      <!-- 로딩 및 상태 표시 -->
      <div v-if="isLoading || isTranscribing || isInitializing" class="loading">
        <div class="spinner"></div>
        <p>{{ loadingMessage }}</p>
      </div>

      <!-- 저장된 녹음 목록 -->
      <div class="saved-recordings-section" v-if="savedRecordings.length > 0">
        <div class="recordings-header">
          <h2>저장된 녹음 ({{ savedRecordings.length }}개)</h2>
          <button @click="clearAllRecordings" class="clear-all-btn">전체 삭제</button>
        </div>
        <div class="recordings-list">
          <div 
            v-for="recording in savedRecordings" 
            :key="recording.id" 
            class="recording-item"
          >
            <div class="recording-header">
              <span class="recording-time">{{ recording.timestamp }}</span>
              <button 
                @click="deleteRecording(recording.id)" 
                class="delete-recording-btn"
                title="삭제"
              >
                🗑️
              </button>
            </div>
            <div class="recording-text">{{ recording.text }}</div>
          </div>
        </div>
      </div>

      <!-- 오류 메시지 -->
      <div v-if="error" class="error">
        <p>{{ error }}</p>
        <button @click="clearError" class="clear-btn">확인</button>
      </div>
    </div>
  </div>
</template>

<script>
import WhisperService from '@/services/WhisperService.js';

export default {
  name: 'WhisperTest',
  data() {
    return {
      selectedFile: null,
      transcription: '',
      isLoading: false,
      isTranscribing: false,
      isRecording: false,
      isInitializing: false,
      error: null,
      loadingMessage: '',
      streamController: null,
      realTimeResults: [],
      isSpacePressed: false,
      spaceRecordingActive: false,
      savedRecordings: []
    }
  },
  async mounted() {
    await this.initializeWhisper();
    this.loadSavedRecordings();
    
    // 컴포넌트에 포커스 설정 (키보드 이벤트를 받기 위해)
    this.$nextTick(() => {
      this.$el.focus();
    });
  },
  beforeUnmount() {
    this.stopRecording();
  },
  methods: {
    async initializeWhisper() {
      try {
        this.isInitializing = true;
        this.loadingMessage = 'Whisper 모델을 로딩 중입니다...';
        
        await WhisperService.initialize();
        
        console.log('✅ Whisper 모델 로딩 완료');
      } catch (error) {
        console.error('❌ Whisper 모델 로딩 실패:', error);
        this.error = 'Whisper 모델 로딩에 실패했습니다: ' + error.message;
      } finally {
        this.isInitializing = false;
      }
    },

    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        this.selectedFile = file;
        this.error = null;
      }
    },

    handleFileDrop(event) {
      event.preventDefault();
      const file = event.dataTransfer.files[0];
      if (file && file.type.startsWith('audio/')) {
        this.selectedFile = file;
        this.error = null;
      } else {
        this.error = '오디오 파일만 업로드할 수 있습니다.';
      }
    },

    async transcribeFile() {
      if (!this.selectedFile) {
        this.error = '파일을 선택해주세요.';
        return;
      }

      try {
        this.isTranscribing = true;
        this.loadingMessage = '오디오 파일을 변환 중입니다...';
        this.transcription = '';

        const result = await WhisperService.transcribeFile(this.selectedFile, {
          language: 'korean',
          return_timestamps: false
        });

        this.transcription = result;
        
      } catch (error) {
        console.error('❌ 변환 오류:', error);
        this.error = '오디오 변환 중 오류가 발생했습니다: ' + error.message;
      } finally {
        this.isTranscribing = false;
      }
    },

    async toggleRecording() {
      if (this.isRecording) {
        this.stopRecording();
      } else {
        await this.startRecording();
      }
    },

    async startRecording() {
      try {
        this.isRecording = true;
        this.realTimeResults = [];
        this.transcription = '';
        this.error = null;

        const stream = await WhisperService.getMicrophoneStream();
        
        this.streamController = await WhisperService.transcribeStream(
          stream,
          (result, error) => {
            if (error) {
              console.error('❌ 실시간 변환 오류:', error);
              this.error = '실시간 변환 중 오류가 발생했습니다: ' + error.message;
              return;
            }
            
            if (result && result.trim()) {
              this.realTimeResults.push(result);
              this.transcription = this.realTimeResults.join(' ');
              console.log('🎤 실시간 결과:', result);
            }
          },
          {
            language: 'korean',
            chunkInterval: 3000 // 3초마다 처리
          }
        );
        
      } catch (error) {
        console.error('❌ 녹음 시작 오류:', error);
        this.error = '마이크 접근 권한이 필요합니다: ' + error.message;
        this.isRecording = false;
      }
    },

    stopRecording() {
      if (this.streamController) {
        this.streamController.stop();
        this.streamController = null;
      }
      this.isRecording = false;
    },

    clearError() {
      this.error = null;
    },

    // 키보드 이벤트 핸들러
    async handleKeyDown(event) {
      // 스페이스바 처리
      if (event.code === 'Space' && !this.isSpacePressed && !this.isInitializing) {
        event.preventDefault();
        this.isSpacePressed = true;
        
        if (!this.spaceRecordingActive) {
          await this.startSpaceRecording();
        }
      }
    },

    async handleKeyUp(event) {
      // 스페이스바 떼기 처리
      if (event.code === 'Space' && this.isSpacePressed) {
        event.preventDefault();
        this.isSpacePressed = false;
        
        if (this.spaceRecordingActive) {
          await this.stopSpaceRecording();
        }
      }
    },

    // 스페이스바 녹음 시작
    async startSpaceRecording() {
      try {
        this.spaceRecordingActive = true;
        this.realTimeResults = [];
        this.transcription = '';
        this.error = null;

        const stream = await WhisperService.getMicrophoneStream();
        
        this.streamController = await WhisperService.transcribeStream(
          stream,
          (result, error) => {
            if (error) {
              console.error('❌ 스페이스바 녹음 변환 오류:', error);
              this.error = '음성 변환 중 오류가 발생했습니다: ' + error.message;
              return;
            }
            
            if (result && result.trim()) {
              this.realTimeResults.push(result);
              this.transcription = this.realTimeResults.join(' ');
              console.log('🎤 스페이스바 녹음 결과:', result);
            }
          },
          {
            language: 'korean',
            chunkInterval: 1000 // 1초마다 처리 (더 빠른 응답)
          }
        );
        
      } catch (error) {
        console.error('❌ 스페이스바 녹음 시작 오류:', error);
        this.error = '마이크 접근 권한이 필요합니다: ' + error.message;
        this.spaceRecordingActive = false;
        this.isSpacePressed = false;
      }
    },

    // 스페이스바 녹음 중지 및 저장
    async stopSpaceRecording() {
      if (this.streamController) {
        this.streamController.stop();
        this.streamController = null;
      }
      
      this.spaceRecordingActive = false;
      
      // 녹음 결과가 있으면 저장
      if (this.transcription && this.transcription.trim()) {
        await this.saveRecording(this.transcription);
      }
    },

    // 녹음 결과 저장
    async saveRecording(text) {
      const recording = {
        id: Date.now(),
        text: text,
        timestamp: new Date().toLocaleString('ko-KR'),
        type: 'space-recording'
      };
      
      this.savedRecordings.unshift(recording);
      
      // 로컬 스토리지에 저장
      try {
        localStorage.setItem('whisper-recordings', JSON.stringify(this.savedRecordings));
        console.log('💾 녹음 결과 저장됨:', recording);
        
        // 저장 완료 알림 (간단히)
        this.showSaveNotification();
      } catch (error) {
        console.error('❌ 저장 실패:', error);
        this.error = '녹음 결과 저장에 실패했습니다.';
      }
    },

    // 저장 완료 알림
    showSaveNotification() {
      // 간단한 알림 표시
      const originalTranscription = this.transcription;
      this.transcription = '💾 저장 완료! ' + originalTranscription;
      
      setTimeout(() => {
        this.transcription = originalTranscription;
      }, 2000);
    },

    // 저장된 녹음 목록 로드
    loadSavedRecordings() {
      try {
        const saved = localStorage.getItem('whisper-recordings');
        if (saved) {
          this.savedRecordings = JSON.parse(saved);
        }
      } catch (error) {
        console.error('❌ 저장된 녹음 로드 실패:', error);
      }
    },

    clearResults() {
      this.transcription = '';
      this.realTimeResults = [];
    },

    // 개별 녹음 삭제
    deleteRecording(id) {
      this.savedRecordings = this.savedRecordings.filter(recording => recording.id !== id);
      try {
        localStorage.setItem('whisper-recordings', JSON.stringify(this.savedRecordings));
        console.log('🗑️ 녹음 삭제됨:', id);
      } catch (error) {
        console.error('❌ 녹음 삭제 실패:', error);
      }
    },

    // 모든 녹음 삭제
    clearAllRecordings() {
      if (confirm('모든 저장된 녹음을 삭제하시겠습니까?')) {
        this.savedRecordings = [];
        try {
          localStorage.removeItem('whisper-recordings');
          console.log('🗑️ 모든 녹음 삭제됨');
        } catch (error) {
          console.error('❌ 전체 녹음 삭제 실패:', error);
        }
      }
    }
  }
}
</script>

<style scoped>
.whisper-test {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
}

.container {
  max-width: 800px;
  margin: 0 auto;
  background: white;
  border-radius: 15px;
  padding: 30px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
}

h1 {
  text-align: center;
  color: #333;
  margin-bottom: 30px;
  font-size: 2.5em;
}

h2 {
  color: #555;
  margin-bottom: 20px;
  border-bottom: 2px solid #667eea;
  padding-bottom: 10px;
}

.upload-section, .recording-section, .results-section {
  margin-bottom: 30px;
  padding: 20px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  background: #f9f9f9;
}

.upload-area {
  border: 2px dashed #667eea;
  border-radius: 10px;
  padding: 40px;
  text-align: center;
  transition: all 0.3s ease;
  cursor: pointer;
}

.upload-area:hover {
  border-color: #764ba2;
  background: #f0f4ff;
}

.upload-btn, .transcribe-btn, .record-btn {
  background: linear-gradient(45deg, #667eea, #764ba2);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 25px;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.3s ease;
  margin: 10px;
}

.upload-btn:hover, .transcribe-btn:hover, .record-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
}

.upload-btn:disabled, .transcribe-btn:disabled, .record-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.record-btn.recording {
  background: linear-gradient(45deg, #ff6b6b, #ee5a52);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}

.recording-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
}

.recording-indicator {
  color: #ff6b6b;
  font-weight: bold;
  font-size: 18px;
}

.file-info {
  margin-top: 20px;
  padding: 15px;
  background: white;
  border-radius: 8px;
  border-left: 4px solid #667eea;
}

.transcription-result {
  background: white;
  padding: 20px;
  border-radius: 10px;
  border-left: 4px solid #4caf50;
  font-size: 16px;
  line-height: 1.6;
  min-height: 60px;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.clear-results-btn {
  background: #ff9800;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
}

.clear-results-btn:hover {
  background: #f57c00;
  transform: translateY(-1px);
}

.real-time-chunks {
  margin-top: 20px;
  padding: 15px;
  background: #f0f8ff;
  border-radius: 8px;
  border-left: 4px solid #2196f3;
}

.real-time-chunks h3 {
  color: #1976d2;
  margin-bottom: 15px;
  font-size: 16px;
}

.chunk-result {
  margin-bottom: 10px;
  padding: 8px 12px;
  background: white;
  border-radius: 5px;
  border-left: 3px solid #2196f3;
}

.chunk-number {
  font-weight: bold;
  color: #1976d2;
  margin-right: 8px;
}

.loading {
  text-align: center;
  padding: 30px;
  background: #f0f4ff;
  border-radius: 10px;
  margin: 20px 0;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error {
  background: #ffebee;
  color: #c62828;
  padding: 20px;
  border-radius: 10px;
  border-left: 4px solid #f44336;
  margin: 20px 0;
}

.clear-btn {
  background: #f44336;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 5px;
  cursor: pointer;
  margin-top: 10px;
}

.clear-btn:hover {
  background: #d32f2f;
}

/* 키보드 가이드 스타일 */
.keyboard-guide {
  background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
  padding: 20px;
  border-radius: 12px;
  margin-bottom: 30px;
  border-left: 5px solid #667eea;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
}

.keyboard-guide p {
  margin: 8px 0;
  font-size: 16px;
}

.space-status {
  padding: 10px 15px;
  border-radius: 25px;
  font-weight: bold;
  text-align: center;
  transition: all 0.3s ease;
  background: #e8f5e8;
  color: #2e7d32;
  border: 2px solid #a5d6a7;
}

.space-status.active {
  background: #ffebee;
  color: #c62828;
  border-color: #ef9a9a;
  animation: pulse-red 1.5s infinite;
}

@keyframes pulse-red {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.02); opacity: 0.9; }
}

/* 저장된 녹음 섹션 스타일 */
.saved-recordings-section {
  margin-bottom: 30px;
  padding: 20px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  background: #fafafa;
}

.recordings-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.clear-all-btn {
  background: #ff5722;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
}

.clear-all-btn:hover {
  background: #d84315;
  transform: translateY(-1px);
}

.recordings-list {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: white;
}

.recording-item {
  padding: 15px;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s ease;
}

.recording-item:last-child {
  border-bottom: none;
}

.recording-item:hover {
  background: #f8f9fa;
}

.recording-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.recording-time {
  font-size: 12px;
  color: #666;
  background: #e9ecef;
  padding: 4px 8px;
  border-radius: 12px;
}

.delete-recording-btn {
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.delete-recording-btn:hover {
  background: #ffebee;
}

.recording-text {
  color: #333;
  line-height: 1.5;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 6px;
  border-left: 3px solid #667eea;
}

@media (max-width: 768px) {
  .container {
    padding: 15px;
    margin: 10px;
  }
  
  h1 {
    font-size: 2em;
  }
  
  .upload-area {
    padding: 20px;
  }
  
  .recording-controls {
    flex-direction: column;
    gap: 10px;
  }
  
  .keyboard-guide {
    padding: 15px;
  }
  
  .keyboard-guide p {
    font-size: 14px;
  }
  
  .recordings-header {
    flex-direction: column;
    gap: 10px;
    align-items: stretch;
  }
  
  .recordings-list {
    max-height: 300px;
  }
  
  .recording-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}
</style>
